import { Component, Inject, Input, OnInit, inject } from '@angular/core';
import { AbstractControl, ControlContainer, NgForm, UntypedFormControl, Validators } from '@angular/forms';
import { AddressTypeService, Classifier, CountryService } from '@nano/common/classifier';
import { CONFIG } from '@nano/core/config';
import { PaymentConfig } from '@nano/payment/payment';
import { PaymentService } from '@nano/payment/payment.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { L10nLocale, L10N_LOCALE, L10nTranslationService } from 'angular-l10n';
import { distinctUntilChanged, merge, Observable, startWith } from 'rxjs';

@Component({
  selector: 'app-payment-address',
  templateUrl: './payment-address.component.html',
  styleUrls: ['./payment-address.component.less'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  standalone: false
})
@UntilDestroy()
export class PaymentAddressComponent implements OnInit {
  private readonly appConfig = inject(CONFIG);

  @Input() addressTypeControl: UntypedFormControl;
  @Input() addressTypeLabel = 'payment.payerInfo.address.type.label';
  @Input() addressTypePlaceholder = 'payment.payerInfo.address.type.placeholder';
  @Input() countryControl: UntypedFormControl;
  @Input() countryLabel = 'payment.receiverInfo.address.country.label';
  @Input() countryPlaceholder = 'payment.receiverInfo.address.country.placeholder';
  @Input() provinceControl: UntypedFormControl;
  @Input() provinceLabel = 'payment.receiverInfo.address.province.label';
  @Input() provincePlaceholder = 'payment.receiverInfo.address.province.placeholder';
  @Input() provinceMaxLength: number;
  @Input() cityControl: UntypedFormControl;
  @Input() cityLabel = 'payment.receiverInfo.address.city.label';
  @Input() cityPlaceholder = 'payment.receiverInfo.address.city.placeholder';
  @Input() cityMaxLength: number;
  @Input() streetControl: UntypedFormControl;
  @Input() streetLabel = 'payment.receiverInfo.address.street.label';
  @Input() streetPlaceholder = 'payment.receiverInfo.address.street.placeholder';
  @Input() streetMaxLength: number;
  @Input() buildingNameControl: UntypedFormControl;
  @Input() buildingNameLabel = 'payment.receiverInfo.address.buildingName.label';
  @Input() buildingNamePlaceholder = 'payment.receiverInfo.address.buildingName.placeholder';
  @Input() buildingNameMaxLength: number;
  @Input() buildingNumberControl: UntypedFormControl;
  @Input() buildingNumberLabel = 'payment.receiverInfo.address.buildingNumber.label';
  @Input() buildingNumberPlaceholder = 'payment.receiverInfo.address.buildingNumber.placeholder';
  @Input() buildingNumberMaxLength: number;
  @Input() postalCodeControl: UntypedFormControl;
  @Input() postalCodeLabel = 'payment.receiverInfo.address.postalCode.label';
  @Input() postalCodePlaceholder = 'payment.receiverInfo.address.postalCode.placeholder';
  @Input() postalCodeMaxLength: number;

  @Input() addressControl: UntypedFormControl;
  @Input() addressLabel = 'payment.receiverInfo.address.address.label';
  @Input() addressPlaceholder = 'payment.receiverInfo.address.address.placeholder';
  @Input() showPostalCodeRequiredInfoIcon = false;
  @Input() addressFieldsMandatory = false;
  @Input() structuredAddressEnabled = false;

  readonly config: PaymentConfig = this.paymentService.config;
  readonly addressTypes$: Observable<Classifier[]>;
  readonly countries$: Observable<Classifier[]>;

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private addressTypeService: AddressTypeService,
    private countryService: CountryService,
    private paymentService: PaymentService,
    private translationService: L10nTranslationService
  ) {
    this.addressTypes$ = this.addressTypeService.addressTypes$;
    this.countries$ = this.countryService.worldwideCountries$;
  }

  get showPostalCodeRequiredMark(): boolean {
    return this.appConfig.markRequiredFormFields && this.showPostalCodeRequiredInfoIcon && !!this.postalCodeControl?.hasValidator(Validators.required);
  }

  get showPostalCodeRequiredInfo(): boolean {
    if (!this.showPostalCodeRequiredInfoIcon || !this.postalCodeControl?.hasValidator(Validators.required)) {
      return false;
    }

    const translated = this.translationService.translate('payment.receiverInfo.address.postalCode.noPostalCodeInfo', this.locale.language) as string;
    return translated !== 'payment.receiverInfo.address.postalCode.noPostalCodeInfo';
  }

  ngOnInit(): void {
    if (this.structuredAddressEnabled && !this.addressFieldsMandatory) {
      this.initStructuredAddressValidators();
    } else {
      this.initAddressControls();
      this.updateRequiredValidators();
    }
  }

  private initStructuredAddressValidators(): void {
    const updateValidators = () => {
      const controls = [
        this.countryControl,
        this.cityControl,
        this.provinceControl,
        this.postalCodeControl,
        this.buildingNumberControl,
        this.buildingNameControl,
        this.streetControl
      ];
      controls.forEach((control) => {
        control.removeValidators(Validators.required);
        control.updateValueAndValidity();
      });
      const anyControlHasValue = controls.some((control) => !!control.value);

      if (anyControlHasValue) {
        this.countryControl.addValidators(Validators.required);
        this.cityControl.addValidators(Validators.required);
      } else {
        this.countryControl.removeValidators(Validators.required);
        this.cityControl.removeValidators(Validators.required);
      }
      this.countryControl.updateValueAndValidity();
      this.cityControl.updateValueAndValidity();
    };

    updateValidators();

    merge(
      this.countryControl.valueChanges.pipe(distinctUntilChanged()),
      this.cityControl.valueChanges.pipe(distinctUntilChanged()),
      this.provinceControl.valueChanges.pipe(distinctUntilChanged()),
      this.postalCodeControl.valueChanges.pipe(distinctUntilChanged()),
      this.buildingNumberControl.valueChanges.pipe(distinctUntilChanged()),
      this.buildingNameControl.valueChanges.pipe(distinctUntilChanged()),
      this.streetControl.valueChanges.pipe(distinctUntilChanged())
    )
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        updateValidators();
      });
  }

  private initAddressControls(): void {
    const addressControls = this.getAddressControls();

    addressControls.forEach((control) =>
      control.valueChanges.pipe(startWith(control), untilDestroyed(this), distinctUntilChanged()).subscribe(() => {
        this.updateRequiredValidators();
      })
    );
  }

  private getAddressControls(): AbstractControl[] {
    return [this.countryControl, this.cityControl, this.provinceControl, this.postalCodeControl].filter((control) => !!control);
  }

  private updateRequiredValidators(): void {
    const addressControls = this.getAddressControls();
    const hasRequiredValidator = this.addressFieldsMandatory || addressControls.some((control) => !!control.value);

    addressControls.forEach((control) => {
      hasRequiredValidator ? control.addValidators(Validators.required) : control.removeValidators(Validators.required);
      control.updateValueAndValidity();
    });
  }

  diacriticInsensitiveFilter = (search: string, option: { nzLabel: string }): boolean => {
    const normalize = (str: string) =>
      (str || '')
        .toLocaleLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    const normalizedOption = normalize(option.nzLabel);
    const normalizedSearch = normalize(search);
    return normalizedOption.includes(normalizedSearch);
  };
}
